/**
 * Select — native `<select>` styled to match Business design tokens.
 *
 * We use a native element (not a Radix popover) because:
 *   - Native controls give proper mobile/tablet keyboards and touch behaviour
 *   - Accessibility is guaranteed out of the box
 *   - Performance: no extra JS for a common control
 *
 * Tablet-first invariants:
 *   - min-height 56px touch target
 *   - Visible focus ring; invalid border state
 */
import { forwardRef, type SelectHTMLAttributes } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import { cn } from '../utils/cn';

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, invalid, children, ...rest }, ref) => (
    <div className="relative">
      <select
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(
          'w-full min-h-biz-touch pl-biz-content-lg pr-10 py-biz-content',
          'bg-biz-background text-biz-foreground',
          'border border-biz-input rounded-biz-input appearance-none',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring focus-visible:border-biz-primary',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'transition-colors duration-biz-fast',
          invalid && 'border-biz-danger focus-visible:ring-biz-danger',
          className,
        )}
        {...rest}
      >
        {children}
      </select>
      <CaretDown
        size={16}
        weight="regular"
        aria-hidden="true"
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-biz-muted-foreground"
      />
    </div>
  ),
);
Select.displayName = 'Select';
